import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BASEMAP_ADDON_GIS } from '@rsos/constants/basemapAddonNames';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import {
  clearLayerMaps,
  fetchGISServices,
  GISServiceTypes,
  gisLayerFor,
  loadGISLayer,
  resetLayerLoadedState,
  selectGISServices,
  selectGISLayersRaw,
} from '@rsos/reduxDataStore';
import { selectMapProvider } from '@rsos/sinatra';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';

const TWENTY_FOUR_HOURS = 86400;

/**
 * Check if a service is a GeoComm/NENA EPRC layer (ECC-specific).
 * These include geocomm and nenaeprc domains.
 */
const isGeocommOrNenaService = service => {
  const url = service.gis_service_url?.toLowerCase() || '';
  return url.includes('geocomm') || url.includes('nenaeprc');
};

/**
 * Check if a service should be loaded immediately:
 * - Basemap layers (essential for map display)
 * - GeoComm/NENA layers (ECC-specific, require token auth)
 * - Auto-checked layers (configured to be visible by default)
 */
const shouldLoadImmediately = service => {
  return (
    service.service_type === GISServiceTypes.basemap ||
    isGeocommOrNenaService(service) ||
    service.auto_checked === true
  );
};

/**
 * Hook for managing GIS services and layer loading when the agency has GIS capability enabled.
 *
 * ## Behavior Overview
 *
 * **Initial Mount:**
 * - Fetches GIS service definitions if GIS addon is enabled
 * - Essential layers (basemap, GeoComm/NENA, auto_checked) load immediately
 * - Other layers load on-demand when user clicks them in MapLayersControl
 *
 * **Provider Switching (ESRI <-> Google):**
 * - When switching to Google: layers are deactivated (via clearAllGISServices)
 * - When switching back to ESRI: ESRI layer objects are recreated for the new MapView
 * - Service definitions are preserved in Redux to avoid unnecessary API calls
 *
 * **GIS Addon Toggle:**
 * - When toggled OFF: layers become invisible
 * - When toggled ON: triggers layer reload by resetting loaded state in Redux
 * - Clears stale ESRI layer object references from previous MapView
 *
 * **Token Renewal:**
 * - Automatically renews GIS token before expiration (5 minute threshold)
 *
 * ## Refs
 *
 * - `essentialLayersLoadedRef`: Tri-state ref (null/false/true) for tracking layer loading:
 *   - null: waiting for fetchGISServices to complete
 *   - false: ready to load essential layers
 *   - true: essential layers already loaded
 *
 * - `initialLoadTriggeredRef`: Prevents duplicate fetches within the same mount cycle
 *
 * - `prevMapProviderRef`: Tracks previous map provider to detect provider switches
 */
const useGISToken = () => {
  const dispatch = useDispatch();

  const orgName = useSelector(state => state.sinatra.user.currentOrg.name);
  const mapProvider = useSelector(selectMapProvider);
  const prevMapProviderRef = useRef(mapProvider);

  const hasGISServicesAccess = useSelector(state =>
    checkGISServicesAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);
  const isGISAddonEnabled = mapAddons?.includes(BASEMAP_ADDON_GIS);

  const gisIdentities = useSelector(state => state.gisServices.gisIdentities);
  const services = useSelector(selectGISServices);
  const layers = useSelector(selectGISLayersRaw);
  const gisServicesLoading = useSelector(state => state.gisServices?.loading);

  const essentialLayersLoadedRef = useRef(null);
  const initialLoadTriggeredRef = useRef(false);
  const tokenExpiresIn =
    gisIdentities?.[0]?.tokenExpiresIn || Date.now() + TWENTY_FOUR_HOURS;

  useEffect(() => {
    if (
      !hasGISServicesAccess ||
      !isGISAddonEnabled ||
      initialLoadTriggeredRef.current
    ) {
      return;
    }
    initialLoadTriggeredRef.current = true;
    clearLayerMaps();

    if (services.length > 0) {
      dispatch(resetLayerLoadedState());
      essentialLayersLoadedRef.current = false;
      return;
    }

    essentialLayersLoadedRef.current = null;
    dispatch(fetchGISServices({ orgName, shouldLoadLayers: false }));
  }, [
    dispatch,
    hasGISServicesAccess,
    isGISAddonEnabled,
    orgName,
    services.length,
  ]);

  useEffect(() => {
    if (gisServicesLoading && essentialLayersLoadedRef.current === null) {
      essentialLayersLoadedRef.current = false;
    }

    if (
      gisServicesLoading ||
      essentialLayersLoadedRef.current === null ||
      essentialLayersLoadedRef.current === true ||
      services.length === 0 ||
      layers.length === 0
    ) {
      return;
    }

    const essentialServices = services.filter(shouldLoadImmediately);
    const unloadedEssentialLayers = layers.filter(
      l =>
        (!l.loaded || !gisLayerFor(l)) &&
        !l.loading &&
        essentialServices.some(s => s.gisLayerID === l.id),
    );

    if (unloadedEssentialLayers.length === 0) {
      essentialLayersLoadedRef.current = true;
      return;
    }

    essentialLayersLoadedRef.current = true;

    unloadedEssentialLayers.forEach(layer => {
      const service = essentialServices.find(s => s.gisLayerID === layer.id);
      if (service) {
        dispatch(loadGISLayer({ service }));
      }
    });
  }, [dispatch, gisServicesLoading, services, layers]);

  useEffect(() => {
    const prevProvider = prevMapProviderRef.current;
    prevMapProviderRef.current = mapProvider;

    if (
      hasGISServicesAccess &&
      isGISAddonEnabled &&
      mapProvider === ESRI &&
      prevProvider &&
      prevProvider !== ESRI
    ) {
      clearLayerMaps();
      essentialLayersLoadedRef.current = null;
      initialLoadTriggeredRef.current = true;
      dispatch(fetchGISServices({ orgName, shouldLoadLayers: false }));
    }
  }, [dispatch, hasGISServicesAccess, isGISAddonEnabled, mapProvider, orgName]);

  useEffect(() => {
    const expirationThreshold = 1000 * 60 * 5;
    const expirationTime = tokenExpiresIn * 1000 - expirationThreshold || 0;
    const currentTime = new Date().getTime();
    const shouldRenew = expirationTime > 0 && expirationTime <= currentTime;

    if (hasGISServicesAccess && shouldRenew) {
      const renewGISToken = setTimeout(() => {
        dispatch(fetchGISServices({ orgName, shouldLoadLayers: false }));
      }, expirationTime);

      return () => clearTimeout(renewGISToken);
    }
  }, [dispatch, hasGISServicesAccess, orgName, tokenExpiresIn]);
};

export default useGISToken;
